import * as Sentry from '@sentry/react';
import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router';
import { BaseForm } from 'components/common/forms/BaseForm/BaseForm';
import { useAppDispatch, useAppSelector } from 'hooks/reduxHooks';
import { doLogin, doLoginWitGoogle } from 'store/slices/authSlice';
import notificationHook from 'hooks/notificationHook';
import { LoginFormData } from 'types/auth';
import { loginFormInitValues } from 'constants/initialValues';
import { redirectAfterLogin } from 'hooks/redirectAfterLogin';
import { LoginResponse } from 'api/auth.api';
import { CredentialResponse, GoogleLogin } from '@react-oauth/google';
import { Button, Col, Row } from 'antd';
import 'styles/sass/login.scss';
import { ApiError } from 'types';
import { Input } from 'components/common/inputs/Input/Input';
import { InputPassword } from 'components/common/inputs/InputPassword/InputPassword';
import statusMessages from 'statusMessages';

export const LoginForm: React.FC = () => {
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const googleAuthClientId = useAppSelector(
    (state) => state.config.config.google.authClientId
  );

  const [isLoading, setLoading] = useState(false);
  const isGoogleLoginEnabled = Boolean(googleAuthClientId);

  const reportGoogleLoginFailure = (message: string, error?: unknown): void => {
    Sentry.withScope((scope) => {
      scope.setContext('google_auth', {
        host: window.location.host,
        googleClientConfigured: isGoogleLoginEnabled,
        googleOneTapEnabled: true,
        fedCmPromptMode: 'default',
      });

      if (error) {
        scope.setExtra(
          'googleAuthError',
          error instanceof Error ? error.message : String(error)
        );
      }

      Sentry.captureMessage(message, 'error');
    });
  };

  const handleSubmit = async (values: LoginFormData) => {
    setLoading(true);
    dispatch(doLogin(values))
      .unwrap()
      .then((res: LoginResponse) => {
        if (res.user) {
          const navigatePath = redirectAfterLogin(res.user.role);
          navigate(navigatePath);
        }
      })
      .catch((error) => {
        const currentStatus = (error as ApiError).statusCode;
        notificationHook({
          type: 'error',
          message: statusMessages.user[
            currentStatus ? currentStatus : 500
          ] as string,
        });
        setLoading(false);
      });
  };

  const processGoogleResponse = async (response: CredentialResponse) => {
    if (response && response.credential) {
      dispatch(doLoginWitGoogle({ token: response.credential }))
        .unwrap()
        .then((res: LoginResponse) => {
          if (res.user) {
            const navigatePath = redirectAfterLogin(res.user.role);
            navigate(navigatePath);
          }
        })
        .catch((error) => {
          notificationHook({
            type: 'error',
            message: 'Ocurrió un error al iniciar sesión con Google',
          });
          reportGoogleLoginFailure(
            'google sign-in backend exchange failed',
            error
          );
        });
    }
  };

  return (
    <div className="form-wrapper">
      <BaseForm
        layout="vertical"
        onFinish={handleSubmit}
        requiredMark="optional"
        initialValues={loginFormInitValues}
      >
        <h1 className="title">Ingresar</h1>
        <Row justify="center" className="mt-4 mb-5">
          {isGoogleLoginEnabled && (
            <Col>
              <GoogleLogin
                onSuccess={(e) => processGoogleResponse(e)}
                onError={() => {
                  notificationHook({
                    type: 'error',
                    message: 'Error al iniciar sesión con Google',
                    description: 'Por favor, inténtalo de nuevo más tarde.',
                  });
                  reportGoogleLoginFailure(
                    'google sign-in initialization failed'
                  );
                }}
                useOneTap
              />
            </Col>
          )}
        </Row>
        <Col>
          <p>O ingresa con tu email y contraseña</p>
          <BaseForm.Item
            className="form-item"
            name="email"
            label="Email"
            rules={[
              { required: true, message: `Este campo es obligatorio` },
              {
                type: 'email',
                message: `Ingresa una dirección de correo válida`,
              },
            ]}
          >
            <Input className="form-input" placeholder={`Email`} />
          </BaseForm.Item>
          <BaseForm.Item
            className="form-item"
            label={`Contraseña`}
            name="password"
            rules={[{ required: true, message: `Este campo es obligatorio` }]}
          >
            <InputPassword
              className="form-input-password"
              placeholder={`Contraseña`}
            />
          </BaseForm.Item>
        </Col>
        <Row>
          <Col>
            <Link className="forget-link" to="/auth/forgot-password">
              Recuperar contraseña
            </Link>
          </Col>
        </Row>
        <Row justify="center" className="mt-5">
          <Col>
            <Button
              type="primary"
              htmlType="submit"
              loading={isLoading}
              className="submit-btn"
            >
              Ingresar
            </Button>
          </Col>
        </Row>
      </BaseForm>
    </div>
  );
};
